import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import lastPubliIcon from '../../../assets/lastPubli.svg';
import publicationsLogo from '../../../assets/publi.svg';
import Spinner from '../../../spinner/Spinner';
import mediaUrl from '../../../service/mediaUrl';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { DATE_FORMAT_ISO_DAY } from '../../../constants/dateFormat';
import time from '../../../assets/sso/time.svg';
import { selectIsSsoUser, selectUserId } from '../../../selectors/userSelectors';

interface Publication {
    media: string;
    extension: string;
    title: string;
    content: string;
    status: string;
    createdAt: string;
    publicationDate: string;
    id: number;
}

function LastPublications() {
    const userId = useSelector(selectUserId);
    const isSsoUser = useSelector(selectIsSsoUser);
    const [numberOfPublications] = useState(4);
    const today = new Date();
    const formattedDate = format(today, DATE_FORMAT_ISO_DAY);
    const [publications, setPublications] = useState<Publication[]>([]);

    const formatDate = (date: string): string => {
        const publiDate = new Date(date);
        return format(publiDate, 'dd/MM/yyyy');
    };

    const baseUrl =
        userId === undefined
            ? undefined
            : `/api/publications-all-accessible-for-` +
              (isSsoUser ? `user/${userId}` : 'admin') +
              `?limit=${numberOfPublications}&status=2&pubDateInf=${formattedDate}&pubDate=desc`;

    const loadPublications = useCallback(async (url: string) => {
        try {
            const response = await axios.get<Publication[]>(url);
            setPublications(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des dernières publications :', error);
        }
    }, []);

    useEffect(() => {
        if (baseUrl === undefined) {
            return;
        }

        void loadPublications(baseUrl);
    }, [baseUrl, loadPublications]);

    return (
        <div
            className="shadow-lg flex flex-col w-full h-full rounded-2xl bg-white px-5 py-6 mb-6"
            style={{ minHeight: '710px' }}
        >
            <div className="flex items-center justify-start gap-2 w-full mb-5">
                <img className="h-[40px]" src={lastPubliIcon} alt="last publication icon" />
                <div
                    className={
                        'flex-1 justify-center text-neutral-900 font-semibold leading-normal ' +
                        (!isSsoUser ? 'text-heavy' : 'text-medium')
                    }
                >
                    Dernières publications
                </div>
            </div>

            <div className="flex-1 flex flex-col gap-10 overflow-y-auto h-auto">
                <Spinner loading={publications.length === 0} />
                {publications.map((publi: Publication, index: number) => {
                    const url = mediaUrl(publi);
                    return (
                        <div
                            key={publi.id || index}
                            className="flex gap-3 overflow-hidden h-[110px] text-left flex-shrink-0"
                        >
                            <img
                                className="w-[92px] h-[110px] flex-shrink-0 object-cover rounded-lg"
                                src={url}
                                alt="illustration"
                            />
                            <div className="flex-1 flex flex-col min-w-0">
                                <div className="text-[14px] leading-[20px] font-medium text-dark-gray line-clamp-1">
                                    {publi?.title}
                                </div>

                                <div className="flex items-center gap-1.5 text-gray leading-[10px] text-[10px] pt-[2px] pb-[8px]">
                                    <img className="w-3 h-3" src={time} alt="clock logo" />
                                    <span>{formatDate(publi.publicationDate)}</span>
                                </div>

                                <div className="text-gray text-[12px] leading-[16px] line-clamp-4 overflow-hidden">
                                    {publi?.content}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {publications.length > 0 && (
                <Link
                    className="mt-5 text-black px-4 py-2 text-medium leading-[1.5] rounded-[1.5rem] font-medium bg-[#F2F6CC] flex justify-center items-center gap-2 flex-shrink-0"
                    to={'/publications'}
                >
                    <img src={publicationsLogo} alt="publications" /> Voir les publications
                </Link>
            )}
        </div>
    );
}

export default LastPublications;
